// eslint-disable-next-line @github-ui/github-monorepo/prefer-github-ui-react-router
import {type To, createPath} from 'react-router'
import {usePreloadRoute} from './use-preload-route'
import {useCallback} from 'react'

const timeoutMap = new WeakMap<EventTarget, ReturnType<typeof setTimeout>>()

const DEFAULT_INTENT_PRELOAD_DELAY = 100
const EMPTY_PROPS = {}

export type PreloadProps = {
  preload?: false | 'intent' // TODO: add more options like 'mount' or 'render'
  preloadDelay?: number
}

// based on https://github.com/TanStack/router/blob/24cc08f87e954f2f2f891cf6870349553c9d4eb0/packages/react-router/src/link.tsx#L43
export function useLinkPreloadProps({
  to,
  preload = false,
  disabled,
  preloadDelay = DEFAULT_INTENT_PRELOAD_DELAY,
}: {
  to: To
  disabled: boolean
} & PreloadProps) {
  const preloadRoute = usePreloadRoute()
  const href = typeof to === 'string' ? to : createPath(to)

  const doPreload = useCallback(() => {
    preloadRoute(href)
  }, [preloadRoute, href])

  if (disabled || preload === false) {
    return EMPTY_PROPS
  }

  // When intent preload is enabled, we fire the preload after a short delay when the user hovers or focuses the link.
  // If the user leaves the link before the delay is up, we cancel the preload.
  const enqueueIntentPreload = (e: React.MouseEvent | React.FocusEvent) => {
    if (preload !== 'intent') return

    if (!preloadDelay) {
      doPreload()
      return
    }

    const eventTarget = e.currentTarget

    if (timeoutMap.has(eventTarget)) {
      return
    }

    const id = setTimeout(() => {
      timeoutMap.delete(eventTarget)
      doPreload()
    }, preloadDelay)
    timeoutMap.set(eventTarget, id)
  }

  // Touchstart prefetches immediately since it's a clear signal of user intent.
  const handleTouchStart = () => {
    if (preload !== 'intent') return
    doPreload()
  }

  // Clear the timeout when the user leaves the link or blurs it before the preload delay is up.
  const handleLeave = (e: React.MouseEvent | React.FocusEvent) => {
    if (!preload || !preloadDelay) return
    const eventTarget = e.currentTarget
    const id = timeoutMap.get(eventTarget)
    if (id) {
      clearTimeout(id)
      timeoutMap.delete(eventTarget)
    }
  }

  return {
    onMouseEnter: enqueueIntentPreload,
    onFocus: enqueueIntentPreload,
    onTouchStart: handleTouchStart,
    onMouseLeave: handleLeave,
    onBlur: handleLeave,
  }
}
